import React, { ReactNode } from 'react';
import { InsightDashboard, InsightDashboardFilters, InsightDashboardSettingKeys, HistoricalUtrsQueryByCodeParams, HistoricalUtrsResponse } from '@g17eco/types/insight-custom-dashboard';
import { useAppSelector } from '../../../reducers';
import { isStaff } from '../../../selectors/user';
import { IntegrationSource } from '@g17eco/types/app-integration';
import type { DashboardSettingsProps } from '@features/custom-dashboard';
import { UniversalTrackerModalServiceUtrv } from '@g17eco/types/universalTrackerValue';

interface CustomDashboardProps {
  children: ReactNode;
  getHistoricalUtrsByCodes: (params: HistoricalUtrsQueryByCodeParams) => Promise<HistoricalUtrsResponse>;
  duplicateDashboard: () => Promise<InsightDashboard>;
  integrationProviders?: IntegrationSource[];
  hideOptions?: InsightDashboardSettingKeys[];
  hideShareButton?: boolean;
  hideQuestionReference?: boolean;
  readOnly?: boolean;
  components?: {
    [key in InsightDashboardFilters]?: (props: DashboardSettingsProps) => React.ReactNode;
  };
  // Only used with CT to redirect to question view
  redirectUtrvSource?: (utrv: UniversalTrackerModalServiceUtrv) => void;
}

export interface InsightDashboardContextProps extends Omit<CustomDashboardProps, 'children'> {
  userIsStaff: boolean;
}

const initialState: InsightDashboardContextProps = {
  getHistoricalUtrsByCodes: () => Promise.resolve({ utrsData: [], valueLists: [] }),
  duplicateDashboard: () => Promise.resolve({} as InsightDashboard),
  hideOptions: [],
  hideShareButton: false,
  hideQuestionReference: false,
  readOnly: false,
  userIsStaff: false,
  redirectUtrvSource: () => {},
};

export const CustomDashboardContext = React.createContext<InsightDashboardContextProps>(initialState);

export const CustomDashboardWrapper = ({
  getHistoricalUtrsByCodes,
  duplicateDashboard,
  hideOptions,
  hideQuestionReference,
  hideShareButton,
  readOnly,
  integrationProviders,
  children,
  components,
  redirectUtrvSource,
}: CustomDashboardProps) => {
  // used to put the multiple utrs under staff flag for now
  const userIsStaff = useAppSelector(isStaff);
  return (
    <CustomDashboardContext.Provider
      value={{
        getHistoricalUtrsByCodes,
        duplicateDashboard,
        integrationProviders,
        hideOptions,
        hideQuestionReference,
        hideShareButton,
        readOnly,
        userIsStaff,
        components,
        redirectUtrvSource,
      }}
    >
      {children}
    </CustomDashboardContext.Provider>
  );
};
